import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import parse from "html-react-parser";
import { useEffect, useMemo, useRef, useState } from "react";
import RedemptionLocationPicker from "@/components/redemption/RedemptionLocationPicker";
import { useIsMobile, useIsTablet, useMediaQuery } from "@/hooks/useMediaQuery";
import type { Product, VariantSelections } from "@/interfaces/product";
import type { CustomLocation } from "@/interfaces/redemption";
import { displayProductName } from "@/utils/product-helper";
import { giftKind } from "@/utils/redemption-helper";
import {
  getProductAvailabilityStatus,
  getProductVariantOptions,
  getSelectableValues,
  getVariantOptionKey,
  getVisibleVariantOptions,
  pickDefaultVariant,
  resolveProductVariant,
  selectionsFromVariant,
  showsColorPicker,
} from "@/utils/variantResolution";
import DetailsPanel from "./DetailsPanel";
import ImpactPanel from "./ImpactPanel";
import ProductDetailErrorState from "./ProductDetailErrorState";
import ProductDetailSkeleton from "./ProductDetailSkeleton";
import StoryPanel from "./StoryPanel";
import { DotsIndicator, ThumbnailCarousel } from "./ThumbnailCarousel";

// ─── Types ─────────────────────────────────────────────────────────────────────

type ActiveTab = "details" | "impact" | "story";

export interface ProductDetailSlideInProps {
  open: boolean;
  product: Product | null;
  loading?: boolean;
  error?: boolean;
  onClose: () => void;
  /** The primary action. Receives the currently-selected variant id so the
   *  recipient flow knows exactly which variant to redeem. Consumers that don't
   *  need it (campaign builder / storefront) can ignore the argument. In `"cause"`
   *  mode there is no variant to resolve, so it receives `product.id` instead.
   *  A Well Told map product (`product.customLocation`) also hands back the recipient's
   *  chosen custom location — the CTA is held until one is set, so it is never missing. */
  onSendGift: (variantId: string, customLocation?: CustomLocation) => void;
  /**
   * What the panel is showing, and therefore which controls it grows.
   *
   * - `"default"` — a catalogue product: colour swatches, variant dropdowns and a
   *   "Choose this gift" CTA carrying the resolved variant id.
   * - `"campaignBuilder"` — the same product with no CTA at all: the buyer is picking a budget,
   *   not a gift, so there is no action to offer. Variant controls DO render (a sender browsing
   *   the grid still needs to see that a gift comes in four colours); the selection is display
   *   only and dies with the panel.
   * - `"cause"` — a charity projected onto `Product`. A cause has no `colors` and no
   *   `variantOptions`, so every
   *   variant surface is off and the resolution helpers are fed `null`; the CTA becomes
   *   "Add this cause" and hands back `product.id`. A cause has NO tab strip at all — its
   *   description prints as plain prose under the CTA, see `causeBody` below.
   * - `"collections"` — the Collections tier detail. Variant controls render as above, again for
   *   display only: the CTA reads "Send in Collection" (paper-plane icon) and hands back
   *   `product.id`, because the buyer is picking a tier, not a variant. The page's `onSendGift`
   *   opens the Campaign Builder at Step 1 with the tier pre-selected.
   */
  mode?: "default" | "campaignBuilder" | "cause" | "collections";
  /**
   * `"cause"` mode only: whether this cause is already in the campaign's cause set. The
   * prototype's panel button is an add/remove TOGGLE reflecting membership
   * (`updateCsiSelectBtn`, Campaign-Builder-Master-Prototype-Stage2-Revisions.html
   * ~line 12631), so `true` flips the CTA to "Added — remove" with a check. `onSendGift`
   * fires either way — it is the toggle, and the caller decides which direction that is.
   * Ignored outside cause mode, where there is nothing to be a member of.
   */
  ctaSelected?: boolean;
  /**
   * Optional soft tip rendered in gray directly under the CTA. Collections mode uses it for
   * "This gift will be included in the $N Collection"; omitted everywhere else → no tip.
   */
  ctaNote?: string;
  /** Drop the primary action (and its note) for a caller who can't take it, e.g. a View Only role. */
  hideCta?: boolean;
  /** Tab to open on. Defaults to "details"; the recipient flow passes "impact". */
  initialTab?: ActiveTab;
  /**
   * Mobile presentation. `"drawer"` (default) keeps the full-screen right-anchored
   * slide-in on phones. `"sheet"` opts into the redemption-flow bottom "gift detail
   * sheet": rises from the bottom over a peek of the page, grabber + solid header,
   * scroll-as-one body, and a sticky thumb-zone CTA. Only the recipient flow passes
   * `"sheet"`; the campaign builder + storefront stay on `"drawer"`. Desktop is
   * unaffected either way (both render the right drawer).
   */
  mobilePresentation?: "drawer" | "sheet";
  /** Raises the drawer above a host overlay (the builder's fullscreen preview). Default: MUI's drawer z-index. */
  zIndex?: number;
}

// ─── Tabs ──────────────────────────────────────────────────────────────────────

/**
 * The tab strip, in order; `productDetailTabs` drops Story Card for charity gifts and for any
 * product with no story-card image (`storyImage` blank). Each entry's markup (`data-testid` /
 * `id` / `aria-controls`) is unchanged.
 */
const TAB_DEFS: { value: ActiveTab; label: string }[] = [
  { value: "details", label: "Details" },
  { value: "impact", label: "Impact" },
  { value: "story", label: "Story Card" },
];

export function productDetailTabs(product: Product | null): { value: ActiveTab; label: string }[] {
  if (!product) return TAB_DEFS;
  const hasStoryCard = (product.storyImage ?? "").trim() !== "";
  return giftKind(product) === "charity" || !hasStoryCard
    ? TAB_DEFS.filter((tab) => tab.value !== "story")
    : TAB_DEFS;
}

export function resolveActiveTab(
  tabs: { value: ActiveTab; label: string }[],
  requested: ActiveTab,
): ActiveTab {
  return tabs.some((tab) => tab.value === requested) ? requested : (tabs[0]?.value ?? "details");
}

const tabSx = {
  fontSize: "14px",
  fontWeight: 600,
  textTransform: "none" as const,
  letterSpacing: "normal",
  minWidth: 56,
  px: 1,
  pt: 1,
  pb: "14px",
  minHeight: "unset",
  color: "var(--color-graphite)",
  "&.Mui-selected": {
    // Rodeo-black label (not dust — dust text is only ~2.8:1) + the dust underline
    // carries the active state. Matches the preview's .lpd-tab.active treatment.
    color: "var(--color-rodeo-black)",
    bgcolor: "transparent",
  },
  "&:focus-visible": {
    outline: "2px solid var(--color-dust-ink)",
    outlineOffset: "2px",
  },
};

const isValidCssColor = (value: string) => {
  const s = new Option().style;
  s.color = value;
  return s.color !== "";
};

const needsBorder = (value: string) => {
  if (!isValidCssColor(value)) return true;

  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) return false;

  ctx.fillStyle = value;
  const computed = ctx.fillStyle;

  return computed === "#ffffff" || computed === "rgb(255, 255, 255)";
};

// ─── Main component ────────────────────────────────────────────────────────────

export default function ProductDetailSlideIn({
  open,
  product,
  loading = false,
  error = false,
  onClose,
  onSendGift,
  mode = "default",
  ctaSelected = false,
  ctaNote,
  hideCta = false,
  initialTab = "details",
  mobilePresentation = "drawer",
  zIndex,
}: ProductDetailSlideInProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>(initialTab);
  const [activeImage, setActiveImage] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedVariantValues, setSelectedVariantValues] = useState<VariantSelections>({});
  const [carouselOffset, setCarouselOffset] = useState(0);
  const [carouselVisibleCount, setCarouselVisibleCount] = useState(5);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [sheetScrolled, setSheetScrolled] = useState(false);
  // Active slide of the sheet's full-bleed swipe gallery (recipient sheet only).
  const [galleryIndex, setGalleryIndex] = useState(0);
  // Well Told map products: the recipient's chosen custom location + the rendered preview
  // photos for it (shown as the main image, v1 swapped `#firstImage` the same way).
  const [customLocation, setCustomLocation] = useState<CustomLocation | null>(null);
  const [previewImages, setPreviewImages] = useState<string[] | null>(null);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  // Bottom "gift detail sheet" only when a sheet-mode consumer (the recipient flow)
  // is on a phone. Desktop + drawer-mode consumers keep the right-anchored slide-in.
  const asSheet = isMobile && mobilePresentation === "sheet";
  const isRecipientFlow = mobilePresentation === "sheet";

  const isCause = mode === "cause";
  // Collections tier detail: the CTA opens the builder with the tier pre-selected (the page owns
  // that action via onSendGift), and it carries the product id, never a variant id — see ctaValue.
  const isCollections = mode === "collections";
  /**
   * What the variant-resolution helpers see. A cause has no `colors` and no `variantOptions`, so
   * instead of sprinkling mode checks through the pipeline, cause mode feeds every helper `null`.
   * All of them short-circuit on a product with no combinations — `resolveProductVariant` returns
   * "not unavailable", `getSelectableValues` an empty map, `getProductAvailabilityStatus`
   * `{ shouldAutoSelect: false, isEntirelyOutOfStock: false }` — so a cause can never end up with
   * an "Out of stock" chip, a disabled swatch or a CTA blocked on a selection that doesn't exist.
   */
  const variantProduct = isCause ? null : product;
  /**
   * Swatches and dropdowns render wherever there is a product to show them for — everywhere but
   * `cause`, which projects a charity onto `Product` and has nothing to pick.
   *
   * They used to be `default`-only, on the reasoning that the campaign builder's buyer picks a
   * budget rather than a gift. Senders read that absence as missing data instead of as "not your
   * choice to make" and reported it twice, so the picker is back on every surface. What differs
   * is what the choice is FOR, and that lives in `ctaValue`, not here: only the recipient's flow
   * hands back a variant id. The builder has no CTA at all, and the tier detail's carries the
   * product id — so a colour picked while browsing changes the image and nothing else, and
   * closing the panel discards it.
   */
  const showVariantControls = !isCause;
  /** The campaign builder is the one surface with no primary action at all. */
  const showCta = mode !== "campaignBuilder" && !hideCta;
  /**
   * The custom-location step is a recipient input, not a browsing control: only the recipient
   * flow (`default`) asks for it. A sender previewing the catalogue in the builder sees the
   * product without it — there is no order to attach a location to there.
   */
  const locationRequirement = mode === "default" ? product?.customLocation : undefined;
  const locationMissing = !!locationRequirement && !customLocation;

  // Focus the trigger element on close
  const triggerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement as HTMLElement;
    } else if (triggerRef.current) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [open]);

  // Reset tab + active image + selected color + selected variant when the drawer closes
  useEffect(() => {
    if (!open) {
      setActiveTab(initialTab);
      setActiveImage(null);
      setSelectedColor(null);
      setSelectedVariantValues({});
      setCarouselOffset(0);
      setCarouselVisibleCount(5);
      setHasInteracted(false);
      setSheetScrolled(false);
      setGalleryIndex(0);
      setCustomLocation(null);
      setPreviewImages(null);
    } else if (product) {
      setHasInteracted(getProductAvailabilityStatus(variantProduct).shouldAutoSelect);
    }
  }, [open, product, variantProduct, initialTab]);

  // Reset active image when a different product is selected (drawer may stay open)
  useEffect(() => {
    if (!product) return;

    setActiveImage(null);
    setSelectedColor(null);
    setSelectedVariantValues({});
    setCarouselOffset(0);
    setGalleryIndex(0);
    setCustomLocation(null);
    setPreviewImages(null);
    setHasInteracted(getProductAvailabilityStatus(variantProduct).shouldAutoSelect);
  }, [product, variantProduct]);

  const defaultVariant = useMemo(() => {
    if (!hasInteracted) return null;
    return pickDefaultVariant(variantProduct);
  }, [variantProduct, hasInteracted]);

  const defaultSelections = useMemo(() => {
    if (!hasInteracted || !defaultVariant) {
      return { selectedColor: null, selections: {} };
    }
    return selectionsFromVariant(variantProduct, defaultVariant);
  }, [variantProduct, defaultVariant, hasInteracted]);

  const activeColor = hasInteracted
    ? (selectedColor ??
      defaultSelections.selectedColor ??
      variantProduct?.colors?.[0]?.value ??
      null)
    : null;

  const variantOptions = useMemo(() => getProductVariantOptions(variantProduct), [variantProduct]);
  /** What the shopper actually sees. `variantOptions` stays whole above because resolution needs
   *  every option to address a variant; these two drop the ones with a single value, which are a
   *  label over a control that can't change anything. */
  const visibleVariantOptions = useMemo(
    () => getVisibleVariantOptions(variantProduct),
    [variantProduct],
  );
  /** The colours the picker renders. Read off `variantProduct` rather than `product` so cause
   *  mode — which feeds every helper `null` — can't surface swatches, and so the block below
   *  needs no null-narrowing of its own. */
  const colorOptions = variantProduct?.colors ?? [];
  const showColorPicker = showVariantControls && showsColorPicker(variantProduct);

  const activeVariantValues = useMemo(() => {
    if (!hasInteracted) {
      return {};
    }
    const values: VariantSelections = { ...defaultSelections.selections, ...selectedVariantValues };
    for (const option of variantOptions) {
      const key = getVariantOptionKey(option);
      values[key] = values[key] || option.values[0]?.title || "";
    }
    return values;
  }, [hasInteracted, defaultSelections.selections, selectedVariantValues, variantOptions]);

  const variantState = useMemo(
    () => resolveProductVariant(variantProduct, activeColor, activeVariantValues),
    [activeColor, activeVariantValues, variantProduct],
  );

  const selectableValues = useMemo(
    () => getSelectableValues(variantProduct, activeColor, activeVariantValues),
    [activeColor, activeVariantValues, variantProduct],
  );

  const displayVariant = variantState.exactVariant ?? variantState.activeVariant;

  const isEntirelyOutOfStock = useMemo(() => {
    return getProductAvailabilityStatus(variantProduct).isEntirelyOutOfStock;
  }, [variantProduct]);

  const displayProduct = useMemo(() => {
    if (!product || !displayVariant) return product;
    const variantImage = displayVariant.imageUrl || product.imageUrl;
    return {
      ...product,
      variantTitle: displayVariant.variantTitle ?? product.variantTitle,
      imageUrl: variantImage,
      thumbnails: [
        ...new Set(
          [
            variantImage,
            ...(displayVariant.thumbnails?.length
              ? displayVariant.thumbnails
              : (product.thumbnails ?? [])),
          ].filter(Boolean),
        ),
      ],
    };
  }, [displayVariant, product]);

  useEffect(() => {
    if (product?.imageUrl) {
      setActiveImage(product.imageUrl);
      setCarouselOffset(0);
    }
  }, [product?.imageUrl]);

  useEffect(() => {
    if (displayVariant?.imageUrl) {
      setActiveImage(displayVariant.imageUrl);
    }
  }, [displayVariant?.imageUrl]);

  // A Well Told preview of the chosen place leads the gallery and becomes the active image
  // when it arrives (or changes with the colour); the catalogue photos stay behind it.
  const thumbnails = displayProduct
    ? [
        ...new Set([
          ...(previewImages ?? []),
          displayProduct.imageUrl,
          ...(displayProduct.thumbnails ?? []),
        ]),
      ]
    : [];
  const previewLead = previewImages?.[0];
  useEffect(() => {
    if (previewLead) setActiveImage(previewLead);
  }, [previewLead]);

  const mainImage = activeImage ?? displayProduct?.imageUrl ?? "";
  /** Label of the chosen colour swatch — fills a colour-dependent Well Told preview key. */
  const activeColorLabel = colorOptions.find((c) => c.value === activeColor)?.label;

  /**
   * Cause mode's body copy, printed as prose in place of the tab strip.
   *
   * The prototype's cause panel (`renderCsiContent`,
   * Campaign-Builder-Master-Prototype-Stage2-Revisions.html ~line 12663) has no tabs: title,
   * organisation, the add button, then the charity's description straight down the column.
   * Tabs were an artefact of reusing the product slide-in, and they split one already-short
   * blurb across two labels — "Details" holding the whole text and "Impact" a sentence
   * fragment. Dropped on review (Ivan, 2026-08-14).
   *
   * `detailsDescription` is the description; `impactDescription` is the fallback for a cause
   * that only carries the short impact line.
   */
  const causeBody = useMemo(() => {
    if (!isCause) return "";
    const source = displayProduct ?? product;
    return (source?.detailsDescription?.trim() || source?.impactDescription?.trim()) ?? "";
  }, [isCause, displayProduct, product]);

  // A requested tab may not exist for this product (no Story Card on a charity or a product
  // without one); the strip and the panel read the resolved value so MUI never gets a `value`
  // matching no child.
  const tabDefs = productDetailTabs(displayProduct ?? product);
  const activeTabInView = resolveActiveTab(tabDefs, activeTab);

  // A cause has no variant to resolve, so its CTA hands back the cause's own id — what the
  // campaign builder's cause picker selects on. Every other mode hands back the resolved variant.
  // Cause + collections CTAs are tier/cause-level, not variant-level, so they carry product.id.
  const ctaValue =
    isCause || isCollections ? (product?.id ?? "") : (displayVariant?.variantId ?? "");
  // Cause mode's CTA is a membership toggle, so an already-picked cause offers to remove
  // itself instead of being added twice.
  const isCauseSelected = isCause && ctaSelected;
  const CtaIcon = isCauseSelected
    ? CheckIcon
    : isCause
      ? VolunteerActivismIcon
      : isCollections
        ? SendOutlinedIcon
        : CardGiftcardIcon;
  const ctaLabel = isCauseSelected
    ? "Added — remove"
    : isCause
      ? "Add this cause"
      : isCollections
        ? "Send in Collection"
        : "Choose this gift";
  // The catalogue CTA carries the resolved variant id. When the recipient hasn't finished
  // picking (no colour/size selected → "missing-selection") or lands on a combination that
  // resolves to nothing, `ctaValue` is empty and `onSendGift("")` is a dead click — the
  // consumer bails on the empty id and the button looks broken. Disable it and say what's
  // needed instead. Cause mode's CTA is a membership toggle carrying the cause id, so it's
  // always actionable and never disabled here.
  //
  // `isUnavailable` is part of the gate, not just `!ctaValue`. A product with no choosable options
  // — one variant, no colour or size to pick — resolves `exactVariant` from an empty selection map,
  // and `resolveProductVariant` returns that variant populated even when it is out of stock (it
  // nulls `activeVariant` instead). So `ctaValue` was truthy on a sold-out gift and the CTA stayed
  // live next to its own "Out of stock" chip — UAT/PROD-08, reported against Bees Knees Raw Honey
  // Comb, which has exactly that shape. Products WITH options never showed it: nothing is
  // auto-selected when every variant is out of stock, so `ctaValue` was already empty.
  //
  // A Well Told map product also holds the CTA until its custom location is set (`locationMissing`);
  // the variant gate is reported first, the location only once the variant is good.
  const ctaDisabled =
    showCta &&
    !isCause &&
    !isCollections &&
    (!ctaValue || variantState.isUnavailable || locationMissing);
  const ctaHint = !ctaDisabled
    ? null
    : !ctaValue || variantState.isUnavailable
      ? variantState.unavailableReason === "out-of-stock" ||
        variantState.unavailableReason === "unavailable-variant"
        ? "This gift is out of stock."
        : variantState.unavailableReason === "invalid-combination"
          ? "That combination isn't available — pick another."
          : "Select your options to continue."
      : locationRequirement?.mode === "state"
        ? "Choose your state to continue."
        : "Choose your custom location to continue.";

  return (
    <Drawer
      data-testid="product-detail-slidein"
      data-active-variant-id={variantState.exactVariant?.variantId ?? ""}
      data-variant-unavailable={variantState.isUnavailable ? "true" : "false"}
      data-unavailable-reason={variantState.unavailableReason ?? ""}
      anchor={asSheet ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      disableScrollLock
      transitionDuration={reduceMotion ? 0 : asSheet ? 420 : undefined}
      sx={{
        zIndex,
        "& .MuiBackdrop-root": {
          // Sheet: a lighter scrim (no blur) so the page peeks behind it, reinforcing
          // "tap up here to go back". Drawer: the heavier blurred scrim as before.
          backdropFilter: asSheet ? "none" : "blur(3px)",
          backgroundColor: asSheet ? "rgba(51, 51, 51, 0.5)" : "rgba(0, 0, 0, 0.7)",
        },
      }}
      slotProps={{
        paper: {
          // Name the dialog for screen readers: the product-name <h2> when present,
          // with a static fallback during loading/error.
          role: "dialog",
          "aria-modal": true,
          "aria-label": isCause ? "Cause details" : "Product details",
          "aria-labelledby": "lpd-product-name",
          sx: {
            width: { xs: "100%", lg: "calc(100% - 380px)", md: "calc(100% - 200px)" },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            ...(asSheet
              ? {
                  // Bottom "gift detail sheet": near-full height, rounded top, warm
                  // surface, clipped so the scrolling body honours the corners.
                  maxHeight: "92dvh",
                  borderRadius: "22px 22px 0 0",
                  border: "none",
                  bgcolor: "var(--color-clear-dust)",
                  boxShadow: "0 -14px 44px rgba(51,51,51,0.20)",
                }
              : {
                  borderRadius: { md: "16px 0 0 16px" },
                  border: "1px solid var(--color-smoke)",
                }),
          },
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          // Sheet: the header floats OVER the full-bleed image (grabber + close as
          // floating controls), pinned to the sheet so it stays put while the body
          // scrolls; transparent, and pointer-transparent so it never eats a swipe
          // (the close re-enables pointer events). Drawer: a normal top bar.
          position: asSheet ? "absolute" : "relative",
          top: asSheet ? 0 : "auto",
          left: asSheet ? 0 : "auto",
          right: asSheet ? 0 : "auto",
          zIndex: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          px: { xs: 2, md: 3 },
          py: { xs: 1.5, md: 2 },
          flexShrink: 0,
          borderBottom: asSheet ? "none" : "1px solid var(--color-smoke)",
          bgcolor: asSheet ? "transparent" : "var(--color-white)",
          boxShadow: "none",
          pointerEvents: asSheet ? "none" : "auto",
          transition: "box-shadow 0.2s",
        }}
      >
        {asSheet && (
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              top: "9px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "40px",
              height: "4px",
              borderRadius: "2px",
              bgcolor: "var(--color-smoke)",
            }}
          />
        )}
        <IconButton
          aria-label={isCause ? "Close cause details" : "Close product details"}
          onClick={onClose}
          sx={{
            color: "var(--color-graphite)",
            width: asSheet ? "44px" : "32px",
            height: asSheet ? "44px" : "32px",
            // Sheet: a frosted-white circle floating over the image; shadow deepens
            // once scrolled so it stays legible over content too.
            borderRadius: asSheet ? "50%" : "var(--radius-btn)",
            border: asSheet ? "none" : "1px solid var(--color-smoke)",
            bgcolor: asSheet ? "rgba(255, 255, 255, 0.85)" : "var(--color-white)",
            boxShadow: asSheet
              ? sheetScrolled
                ? "0 2px 10px rgba(51,51,51,0.28)"
                : "0 1px 6px rgba(51,51,51,0.18)"
              : "none",
            backdropFilter: asSheet ? "blur(4px)" : "none",
            pointerEvents: "auto",
            padding: "0px",
            margin: "0px",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              bgcolor: asSheet ? "var(--color-white)" : "var(--color-wax)",
              borderColor: "var(--color-rodeo-dust)",
              color: "var(--color-rodeo-black)",
            },
            "&:focus-visible": {
              outline: "2px solid var(--color-dust-ink)",
              outlineOffset: "2px",
            },
          }}
        >
          <CloseIcon
            sx={{
              fontSize: "18px",
              color: "inherit",
              marginTop: "-1px",
            }}
          />
        </IconButton>
      </Box>

      {/* ── Body ───────────────────────────────────────────────────── */}
      {loading ? (
        <ProductDetailSkeleton isMobile={isMobile} />
      ) : error ? (
        <ProductDetailErrorState />
      ) : product ? (
        <Box
          onScroll={asSheet ? (e) => setSheetScrolled(e.currentTarget.scrollTop > 4) : undefined}
          sx={{
            display: "flex",
            flex: 1,
            // Sheet: the whole body scrolls as one editorial column (image gives way to
            // content) with the sticky shelf pinned below. Drawer: body is fixed and the
            // inner columns scroll independently — kept byte-for-byte as before.
            overflow: "hidden",
            ...(asSheet
              ? { minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" }
              : {}),
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          {/* Left column: main image + thumbnails */}
          {asSheet ? (
            /* Sheet: full-bleed 1:1 swipe gallery. Horizontal scroll-snap track, one
               square slide per image (contain on white so non-square/secondary images
               never crop), with floating dots for position. */
            <Box sx={{ width: "100%", flexShrink: 0, position: "relative" }}>
              <Box
                role="group"
                aria-label="Product images"
                tabIndex={0}
                onScroll={(e) => {
                  const el = e.currentTarget;
                  const w = el.clientWidth || 1;
                  setGalleryIndex(Math.round(el.scrollLeft / w));
                }}
                sx={{
                  display: "flex",
                  overflowX: "auto",
                  scrollSnapType: "x mandatory",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                {thumbnails.map((src) => (
                  <Box
                    key={src}
                    sx={{
                      flex: "0 0 100%",
                      scrollSnapAlign: "center",
                      aspectRatio: "1 / 1",
                      bgcolor: "var(--color-white)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="img"
                      src={src}
                      alt={displayProduct?.name ?? product.name}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "https://placehold.co/400x400?text=No+image";
                      }}
                      sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  </Box>
                ))}
              </Box>
              {thumbnails.length > 1 && (
                <Box aria-hidden="true" sx={{ position: "absolute", bottom: 8, left: 0, right: 0 }}>
                  <DotsIndicator
                    count={thumbnails.length}
                    activeIndex={galleryIndex}
                    offset={0}
                    visibleCount={thumbnails.length}
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                width: isMobile ? "100%" : isTablet ? "55%" : "56%",
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                px: { xs: 3, lg: 2 },
                pt: 2,
                pb: 2,
                overflowY: isMobile ? "visible" : "auto",
              }}
            >
              {/* Main image */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "var(--color-white)",
                  overflow: "hidden",
                  // Sheet: frame the product so it reads as an object resting on the warm
                  // sheet (matches the preview's `.lpd-main-img` framed treatment).
                  borderRadius: asSheet ? "14px" : "8px",
                  border: asSheet ? "1px solid var(--color-card-border)" : "none",
                  p: asSheet ? "14px" : 0,
                  height: asSheet ? 262 : isMobile ? 280 : 460,
                }}
              >
                <Box
                  component="img"
                  src={mainImage}
                  alt={displayProduct?.name ?? product.name}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "https://placehold.co/400x400?text=No+image";
                  }}
                  sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </Box>

              {/* Thumbnails carousel */}
              {thumbnails.length > 1 && (
                <>
                  <ThumbnailCarousel
                    key={`carousel-${product.id}`}
                    thumbnails={thumbnails}
                    productName={displayProduct?.name ?? product.name}
                    activeImage={activeImage}
                    onSelect={setActiveImage}
                    onOffsetChange={setCarouselOffset}
                    onVisibleCountChange={setCarouselVisibleCount}
                  />
                  <DotsIndicator
                    count={thumbnails.length}
                    activeIndex={activeImage ? Math.max(0, thumbnails.indexOf(activeImage)) : 0}
                    offset={carouselOffset}
                    visibleCount={carouselVisibleCount}
                  />
                </>
              )}
            </Box>
          )}

          {/* Vertical divider (desktop only) */}
          {!isMobile && (
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: "var(--color-smoke)",
                mx: "24px",
                my: "12px",
              }}
            />
          )}

          {/* Right column: name, impact, tabs */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 3,
              px: 3,
              py: 2,
              // Sheet scrolls the whole body as one, so the info column must not be its
              // own scroll container; the drawer keeps it independently scrollable.
              overflowY: asSheet ? "visible" : "auto",
              minWidth: 0,
            }}
          >
            {/* Product name & Out of stock label */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <Typography
                component="h2"
                id="lpd-product-name"
                sx={{
                  fontSize: "30px",
                  fontWeight: 400,
                  lineHeight: 1,
                  letterSpacing: "-0.6px",
                  color: "var(--color-rodeo-black)",
                  fontFamily: "var(--font-crimson)",
                  mb: "-12px",
                }}
              >
                {displayProductName(displayProduct?.name ?? product.name)}
              </Typography>

              {isEntirelyOutOfStock && (
                <Chip
                  label="Out of stock"
                  size="small"
                  sx={{
                    bgcolor: "var(--color-smoke)",
                    color: "var(--color-rodeo-black)",
                    fontWeight: 600,
                    borderRadius: "4px",
                    fontFamily: "var(--font-inter)",
                  }}
                />
              )}
            </Box>

            {/* Impact tag line */}
            {product.impactTag && (
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                {product.impactIcon && (
                  <Box
                    component="img"
                    src={product.impactIcon}
                    alt=""
                    sx={{
                      width: 20,
                      height: 20,
                      objectFit: "contain",
                      flexShrink: 0,
                    }}
                  />
                )}
                <Typography
                  sx={
                    isCause
                      ? {
                          // For a cause `impactTag` is the organisation name, which reads as an
                          // uppercase overline under the title — the treatment of the cause panel
                          // this replaced (the prototype's `.csi-org-label`).
                          fontSize: "11px",
                          fontWeight: 700,
                          letterSpacing: "1.5px",
                          textTransform: "uppercase",
                          color: "var(--color-dark-dust)",
                          lineHeight: 1.4,
                        }
                      : {
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "var(--color-graphite)",
                          lineHeight: 1.4,
                        }
                  }
                >
                  {product.impactTag}
                </Typography>
              </Box>
            )}

            {/* Well Told custom location (recipient flow only) — required before the CTA
                unlocks; sits above the variant controls as in v1's product page. */}
            {locationRequirement && (
              <Box sx={{ mb: "24px" }}>
                <RedemptionLocationPicker
                  key={product.id}
                  location={locationRequirement}
                  value={customLocation}
                  colorLabel={activeColorLabel}
                  onChange={setCustomLocation}
                  onPreview={setPreviewImages}
                />
              </Box>
            )}

            {/* Color picker */}
            {showColorPicker && (
              <Box>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "var(--color-rodeo-black)",
                    letterSpacing: "0.05em",
                    mb: 1,
                  }}
                >
                  <Box component="span" sx={{ color: "var(--color-rodeo-black)" }}>
                    Color:
                  </Box>{" "}
                  <Box component="span" sx={{ color: "var(--color-graphite)" }}>
                    {colorOptions.find((c) => c.value === activeColor)?.label ?? ""}
                  </Box>
                </Typography>
                <Box
                  role="group"
                  aria-label="Select color"
                  sx={{ display: "flex", gap: "24px", flexWrap: "wrap" }}
                >
                  {colorOptions.map((color) => {
                    const isSelected = activeColor === color.value;
                    const colorKey = String(color.position ?? color.optionName ?? "Color");
                    const availableColors = selectableValues.get(colorKey);
                    const isSelectable = !availableColors || availableColors.has(color.label);
                    return (
                      <Box
                        key={`${color.label}-${color.value}`}
                        component="button"
                        onClick={() => {
                          setSelectedColor(color.value);
                          setHasInteracted(true);
                        }}
                        aria-label={`Color ${color.label}`}
                        aria-pressed={isSelected}
                        disabled={!isSelectable}
                        title={isSelectable ? color.label : `Out of Stock - ${color.label}`}
                        sx={{
                          // 34×34px outer circle — white bg, selection ring via border
                          width: 34,
                          height: 34,
                          borderRadius: "50%",
                          bgcolor: "var(--color-white)",
                          // 2px border (transparent→color) + 2px padding = 4px gap, inner = 26px
                          border: !isSelectable
                            ? "2px solid var(--color-rodeo-black)"
                            : isSelected
                              ? "2px solid var(--color-rodeo-black)"
                              : "2px solid transparent",
                          padding: "2px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          cursor: isSelectable ? "pointer" : "not-allowed",
                          opacity: isSelectable ? 1 : 0.45,
                          position: "relative",
                          overflow: asSheet ? "visible" : "hidden",
                          transition: "border-color 0.15s",
                          // Invisible 44×44 tap area: inset is from the padding box (2px border).
                          ...(asSheet
                            ? {
                                "&::after": {
                                  content: '""',
                                  position: "absolute",
                                  inset: "-7px",
                                  borderRadius: "50%",
                                },
                              }
                            : {}),
                          "&:focus-visible": {
                            outline: "2px solid var(--color-rodeo-black)",
                            outlineOffset: "3px",
                          },
                        }}
                      >
                        {/* 26×26px inner colored circle */}
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "50%",
                            position: "relative",
                            ...(isValidCssColor(color.value)
                              ? {
                                  bgcolor: isSelectable ? color.value : "var(--color-smoke)",
                                  border: needsBorder(color.value)
                                    ? "1px solid var(--color-smoke)"
                                    : "none",
                                }
                              : {
                                  backgroundImage: `url(${color.value})`,
                                  backgroundSize: "cover",
                                  backgroundPosition: "center",
                                  filter: isSelectable ? "none" : "grayscale(1)",
                                }),
                          }}
                        />
                        {!isSelectable && (
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              borderRadius: "50%",
                              pointerEvents: "none",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                width: "150%",
                                height: "2px",
                                bgcolor: "var(--color-rodeo-black)",
                                transform: "translate(-50%, -50%) rotate(-45deg)",
                                opacity: 0.75,
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Variant dropdowns */}
            {showVariantControls &&
              visibleVariantOptions.map((option) => {
                const optionKey = getVariantOptionKey(option);
                const availableValues = selectableValues.get(optionKey);
                let selectedValue = "";
                if (hasInteracted) {
                  selectedValue = activeVariantValues[optionKey] || option.values?.[0]?.title || "";
                }
                const longestOptionLabel = Math.max(
                  option.name.length,
                  selectedValue.length,
                  ...option.values.map((value) => value.title.length),
                );
                const selectWidth = `calc(${Math.max(16, longestOptionLabel + 2)}ch + 56px)`;

                return (
                  <FormControl
                    key={option.name}
                    sx={{
                      width: selectWidth,
                      minWidth: "208px",
                      maxWidth: "100%",
                      alignSelf: "flex-start",
                      textTransform: "uppercase",
                    }}
                  >
                    <InputLabel
                      id={`${option.name}-select-label`}
                      sx={{
                        textTransform: "uppercase",
                        fontSize: "13px",
                        letterSpacing: "0.05em",
                        fontFamily: "var(--font-lato)",
                        fontWeight: 500,
                        color: "var(--color-graphite)",
                      }}
                    >
                      {option.name}
                    </InputLabel>

                    <Select
                      labelId={`${option.name}-select-label`}
                      value={selectedValue}
                      label={option.name}
                      onChange={(e) => {
                        setSelectedVariantValues((current) => ({
                          ...current,
                          [optionKey]: e.target.value as string,
                        }));
                        setHasInteracted(true);
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: "12px",
                            marginTop: "8px",
                            border: "1px solid var(--color-dark-dust)",
                            boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                            "& .MuiMenuItem-root": {
                              fontFamily: "var(--font-lato)",
                              textTransform: "uppercase",
                              fontSize: "13px",
                              padding: "10px 16px",
                            },
                          },
                        },
                      }}
                      sx={{
                        width: "100%",
                        height: "42px",
                        minWidth: "208px",
                        maxWidth: "100%",
                        borderRadius: "8px",
                        backgroundColor: "var(--color-white)",
                        fontSize: "14px",
                        fontFamily: "var(--font-lato)",

                        "& .MuiSelect-select": {
                          paddingLeft: "14px",
                          paddingRight: "14px",
                          display: "flex",
                          alignItems: "center",
                          minHeight: "42px !important",
                          whiteSpace: "nowrap",
                          overflow: "visible",
                          textOverflow: "clip",
                        },

                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid var(--color-dark-dust)",
                        },

                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid var(--color-dark-dust)",
                        },

                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid var(--color-dark-dust)",
                        },
                      }}
                    >
                      {option.values.map((value) => {
                        const isSelectable = !availableValues || availableValues.has(value.title);
                        return (
                          <MenuItem
                            key={`${option.name}-${value.title}`}
                            value={value.title}
                            disabled={!isSelectable}
                          >
                            {value.title}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                );
              })}

            {showCta && !asSheet && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "8px",
                  mb: "24px",
                }}
              >
                <Box
                  component="button"
                  type="button"
                  disabled={ctaDisabled}
                  aria-describedby={ctaHint ? "lpd-cta-hint" : undefined}
                  onClick={() => onSendGift(ctaValue, customLocation ?? undefined)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    px: "28px",
                    py: "10px",
                    bgcolor: "var(--color-rodeo-black)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "var(--radius-btn)",
                    fontFamily: "var(--font-lato)",
                    fontSize: "11px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "2px",
                    cursor: ctaDisabled ? "not-allowed" : "pointer",
                    opacity: ctaDisabled ? 0.45 : 1,
                    transition: "opacity 0.15s, background-color 0.15s",
                    "&:hover": { bgcolor: ctaDisabled ? "var(--color-rodeo-black)" : "#1a1a1a" },
                  }}
                >
                  <CtaIcon sx={{ fontSize: 16 }} /> {ctaLabel}
                </Box>
                {ctaHint && (
                  <Typography
                    id="lpd-cta-hint"
                    sx={{
                      fontSize: "12px",
                      color: "var(--color-graphite)",
                      fontFamily: "var(--font-lato)",
                    }}
                  >
                    {ctaHint}
                  </Typography>
                )}
                {ctaNote && (
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "var(--color-graphite)",
                      fontFamily: "var(--font-lato)",
                    }}
                  >
                    {ctaNote}
                  </Typography>
                )}
              </Box>
            )}

            {/* Cause body — prose, no tabs (see `causeBody`). */}
            {isCause && causeBody && (
              <Typography
                component="div"
                sx={{
                  fontSize: "14px",
                  lineHeight: "24px",
                  color: "var(--color-rodeo-black)",
                }}
              >
                {parse(causeBody)}
              </Typography>
            )}

            {/* Tabs */}
            {!isCause && (
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                <Tabs
                  value={activeTabInView}
                  onChange={(_, v: ActiveTab) => setActiveTab(v)}
                  aria-label="Product information tabs"
                  variant={isMobile || isTablet ? "fullWidth" : "standard"}
                  sx={{
                    minHeight: "unset",
                    "& .MuiTabs-indicator": {
                      backgroundColor: "var(--color-dark-dust)",
                      height: "2px",
                    },
                  }}
                >
                  {tabDefs.map(({ value, label }) => (
                    <Tab
                      key={value}
                      data-testid={`tab-${value}`}
                      label={label}
                      value={value}
                      id={`tab-${value}`}
                      aria-controls={`tabpanel-${value}`}
                      sx={
                        asSheet
                          ? { ...tabSx, minHeight: 44 }
                          : isTablet && isRecipientFlow
                            ? { ...tabSx, px: 0.5, whiteSpace: "nowrap" }
                            : tabSx
                      }
                    />
                  ))}
                </Tabs>

                <Box
                  role="tabpanel"
                  id={`tabpanel-${activeTabInView}`}
                  aria-labelledby={`tab-${activeTabInView}`}
                  // Focusable so keyboard users can scroll a long panel that has no
                  // focusable content of its own (WCAG 2.1.1 / ARIA tabpanel practice).
                  tabIndex={0}
                  sx={{
                    mt: "10px",
                    "&:focus-visible": {
                      outline: "2px solid var(--color-dust-ink)",
                      outlineOffset: "2px",
                    },
                  }}
                >
                  {activeTabInView === "details" && (
                    <DetailsPanel product={displayProduct ?? product} />
                  )}
                  {activeTabInView === "impact" && (
                    <ImpactPanel
                      product={displayProduct ?? product}
                      stacked={isTablet && isRecipientFlow}
                      wrapLabels={asSheet}
                    />
                  )}
                  {activeTabInView === "story" && (
                    <StoryPanel product={displayProduct ?? product} />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      ) : null}

      {/* Sticky thumb-zone action shelf (sheet mode only). Pins the primary CTA to the
          bottom of the sheet as a full-width pill; the inline CTA above is hidden when
          asSheet. Safe-area padded for the home indicator. */}
      {asSheet && !loading && !error && product && showCta && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: ctaHint ? "8px" : 0,
            px: "18px",
            pt: "12px",
            pb: "max(14px, env(safe-area-inset-bottom))",
            bgcolor: "var(--color-clear-dust)",
            borderTop: "1px solid var(--color-card-border)",
            boxShadow: "0 -6px 18px rgba(51,51,51,0.05)",
          }}
        >
          {ctaHint && (
            <Typography
              id="lpd-cta-hint"
              sx={{
                fontSize: "12px",
                color: "var(--color-graphite)",
                fontFamily: "var(--font-lato)",
                textAlign: "center",
              }}
            >
              {ctaHint}
            </Typography>
          )}
          <Box
            component="button"
            type="button"
            disabled={ctaDisabled}
            aria-describedby={ctaHint ? "lpd-cta-hint" : undefined}
            onClick={() => onSendGift(ctaValue, customLocation ?? undefined)}
            sx={{
              width: "100%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "9px",
              py: "15px",
              px: "24px",
              border: "none",
              borderRadius: "999px",
              bgcolor: "var(--color-rodeo-black)",
              color: "#fff",
              fontFamily: "var(--font-lato)",
              fontSize: "12px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "1.5px",
              cursor: ctaDisabled ? "not-allowed" : "pointer",
              opacity: ctaDisabled ? 0.5 : 1,
              transition: "opacity 0.15s, background-color 0.15s",
              "&:hover": { bgcolor: ctaDisabled ? "var(--color-rodeo-black)" : "#1a1a1a" },
            }}
          >
            <CtaIcon sx={{ fontSize: 18 }} /> {ctaLabel}
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
